import type {
  ProductInput,
  ProductOutput,
  OrderCodeState,
  OrderCodeOptionsState,
  SelectedAccessoryInput,
  PropertyStateInput,
  AccessoryInfoInput,
} from "../product.types";

export const getInputFromOutput = (
  output: ProductOutput,
  seriesId: number,
): ProductInput => {
  if (!output.product?.currentStrategyId) {
    throw new Error("Current strategy ID is missing in the output");
  }
  return {
    accessoryInfo: getAccessoryInfoFromOutput(output),
    state: {
      orderCodeState: getOrderCodeStateFromOutput(output),
      currentStrategyId: output.product.currentStrategyId,
      selectedAccessories: getSelectedAccessoriesFromOutput(output),
      propertyStates: getPropertyStatesFromOutput(output),
    },
    seriesId,
  };
};

const getAccessoryInfoFromOutput = (
  output: ProductOutput,
): AccessoryInfoInput | null => {
  if (!output.product?.accessoryInfo) {
    return null;
  }
  const accessoryInfo = output.product.accessoryInfo;
  if (!accessoryInfo.accessoryId) {
    throw new Error("Accessory info is missing accessoryId");
  }
  if (!accessoryInfo.accessoryGroupId) {
    throw new Error("Accessory info is missing accessoryGroupId");
  }
  const parent = accessoryInfo.parent;
  if (!parent?.seriesId) {
    throw new Error("Accessory info is missing parent seriesId");
  }
  if (!parent.state?.currentStrategyId) {
    throw new Error("Accessory info is missing parent state currentStrategyId");
  }
  return {
    accessoryId: accessoryInfo.accessoryId,
    accessoryGroupId: accessoryInfo.accessoryGroupId,
    parent: {
      seriesId: parent.seriesId,
      state: {
        orderCodeState: parent.state.orderCodeState,
        currentStrategyId: parent.state.currentStrategyId,
        propertyStates: parent.state.propertyStates?.map(
          (propertyState) =>
            propertyState && {
              id: propertyState.id,
              value: String(propertyState.value),
            },
        ),
      },
    },
  };
};

export const getPropertyStatesFromOutput = (output: ProductOutput) => {
  if (!output.product?.inputs) {
    return [];
  }
  const propertyStates: PropertyStateInput[] = [];
  output.product.inputs.forEach((propertyState) => {
    propertyState?.properties?.forEach((property) => {
      if (property && property.id !== null && property.value !== null) {
        propertyStates.push({
          id: property.id || "",
          value: String(property.value),
        });
      }
    });
  });
  return propertyStates;
};

const getSelectedAccessoriesFromOutput = (
  output: ProductOutput,
): SelectedAccessoryInput[] => {
  const accessories: SelectedAccessoryInput[] = [];
  if (!output.product?.selectedAccessories) {
    return accessories;
  }
  for (const accessory of output.product.selectedAccessories) {
    accessories.push({
      seriesId: accessory?.seriesId,
      pimId: accessory?.pimId,
      currentStrategyId: accessory?.currentStrategyId,
      optionStates: accessory?.optionStates,
      seriesName: accessory?.seriesName,
      accessoryGroupId: accessory?.accessoryGroupId,
      accessoryId: accessory?.accessoryId,
      currentPresentation: accessory?.currentPresentation,
      infoText: accessory?.infoText,
    });
  }
  return accessories;
};

const getOrderCodeStateFromOutput = (output: ProductOutput): OrderCodeState => {
  if (!output?.product?.orderCode?.parts) {
    throw new Error("Order code parts are missing");
  }
  const optionStates: OrderCodeOptionsState = [];
  for (const part of output.product.orderCode.parts) {
    if (part?.options) {
      for (const option of part.options) {
        if (part?.state !== null && part?.state >= 2) continue;
        if (
          option?.state !== null &&
          option?.state !== undefined &&
          option?.state >= 2
        )
          continue;
        if (option && option.currentSignature) {
          optionStates.push({
            partKey: part.key,
            optionKey: option.key,
            signatureKey: option.currentSignature.key,
            signatureValue: option.currentSignature?.value,
            separator: option.separator,
            isSizePart: false,
            signaturePresentation: option.currentSignature?.presentation,
          });
        }
      }
    }
  }
  const state: OrderCodeState = {
    optionStates,
  };
  return state;
};
